import React, { Key, useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogTitle, Slide } from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import clsx from 'clsx'
import { useSelector } from 'react-redux'

import { ButtonSelectInstalmentMonthContentProps, IQuickMonthsChoiceButtonProps, ISelectInstalmentsMonthsProps } from './ChangeInstalmentsMenuProps'
import Button, { AppButtonTypes } from 'components/Buttons/ButtonBase/Button'
import Checkbox from 'components/Checkbox/Checkbox'
import { IInstalmentsMonthsState } from 'store/InstalmentsMonthsSlice/InstalmentsMonthsSliceModels'
import { FULL_OFFERT_MENU_TYPES } from 'components/FullOffertInfoMenu/FullOffertInfoMenuProps'
import FullOffertInfoMenu from 'components/FullOffertInfoMenu/FullOffertInfoMenu'
import { WorkflowTypes } from 'common/workflow'
import { useChangeInstalmentsMenuLogic, useSelectInstalmentsMonthsLogic } from './hooks/useChangeInstalmentsMenuLogic'
import useTextContents from 'common/hooks/useTextContents'
import analyticsService from 'service/AnalyticsService/AnalyticsService'
import { AppState } from 'store'

import ExpandIcon from 'assets/icons/menu-back-icon.svg'
import './ChangeInstalmentsMenuStyles.scss'



const QuickMonthsChoiceButton = (props: IQuickMonthsChoiceButtonProps) => {

   const { maxLoanPeriod, buttonContent, quickMonthChoiceButtonValue, onClick } = props

   if(maxLoanPeriod === null || quickMonthChoiceButtonValue > maxLoanPeriod) {
      return null
   }

   return (
      <Button 
         buttonContent={buttonContent} 
         className='select-instalments-months-quick-choice-button' 
         onClick={onClick} 
      />
   )
}

const ButtonSelectInstalmentMonthContent = (props: ButtonSelectInstalmentMonthContentProps) => {
   return (
      <div 
         className={clsx(
            'select-instalments-months-button-content-wrapper',
            props.disabled &&  'select-instalments-months-button-content-wrapper-disabled',
            props.isButtonSelected && 'select-instalments-months-button-content-wrapper-active',
            props.hasError && 'select-instalments-months-button-content-wrapper-has-error'
         )}
      >
         <Checkbox isChecked={props.isButtonSelected} className='select-instalments-months-button-content-checkbox' />
         <p>{props.label}</p>
      </div>
   )
}

const SelectInstalmentsMonths = (props: ISelectInstalmentsMonthsProps) => {
   
   const { 
      monthsInstalmentTable,
      selectInstalmentsMonthsHeading,
      isCalendarValid,
      isLoading,
      maxLoanPeriod,
      handleRecalculateOffertOnMonthSelect,
      changeInstalmentsMenuTextContents,
      dispatch} = props

   const { quickChoiceButtonsData, handleQuickChoiceButtonClick, handleInstalmentsMonthButton } = useSelectInstalmentsMonthsLogic({
      dispatch: dispatch,
      handleRecalculateOffertOnMonthSelect: handleRecalculateOffertOnMonthSelect,
      monthsInstalmentTable: monthsInstalmentTable
   })

   return (
      <div className='select-instalments-months-outer-wrapper'>
         { <ChangeInstalmentsMenuModalContentLoader isLoading={isLoading} /> }
         <div className='select-instalments-months-buttons-heading'>
            <h4>
               {changeInstalmentsMenuTextContents('modals.changeInstalmentsMenu.calendar.header')}
               <b>{ selectInstalmentsMonthsHeading }</b>
            </h4>
            <FullOffertInfoMenu 
               menuType={FULL_OFFERT_MENU_TYPES.INFO} 
               isLoading={isLoading} 
               className={isLoading ? 'select-instalments-months-icon-content-loading' : ''}
            />
         </div>
         <div className='select-instalments-months-buttons-wrapper'>
            {
               monthsInstalmentTable
               .map((singleMonth: IInstalmentsMonthsState, index: Key) => {
                  return (
                     <Button
                        key={index}
                        uiButtonType={AppButtonTypes.OUTLINED}
                        disabled={singleMonth.installmentNumber > (maxLoanPeriod ?? 0)}
                        buttonContent={
                           <ButtonSelectInstalmentMonthContent 
                              isButtonSelected={singleMonth.isSelected}
                              label={singleMonth.label ?? ""}
                              hasError={singleMonth.hasError}
                              disabled={singleMonth.installmentNumber > (maxLoanPeriod ?? 0)}
                           />
                        } 
                        className={clsx(
                           'select-instalments-months-button',
                           singleMonth.isSelected && 'select-instalments-months-button-active', 
                           singleMonth.hasError && 'select-instalments-months-button-has-error'
                        )} 
                        onClick={() => handleInstalmentsMonthButton(singleMonth.installmentNumber, !singleMonth.isSelected)}
                        id={`button-select-instalments-month-${singleMonth.installmentNumber}`}
                     />
                  )
               })
            }
         </div>
         {
            !isCalendarValid &&
            <p className='select-instalments-months-error-message'> 
               {changeInstalmentsMenuTextContents('modals.changeInstalmentsMenu.calendar.validationErrorText')}
            </p>
         }
         {
            (maxLoanPeriod !== null && quickChoiceButtonsData.TWO <= maxLoanPeriod) &&  
            <div className='select-instalments-months-quick-choice-wrapper-outer'>
               <p>{changeInstalmentsMenuTextContents('modals.changeInstalmentsMenu.calendar.quickChoiceButtons.description')}</p>
               <div className='select-instalments-months-quick-choice-buttons-wrapper'>
                  <QuickMonthsChoiceButton 
                     maxLoanPeriod={maxLoanPeriod}
                     quickMonthChoiceButtonValue={quickChoiceButtonsData.TWO}
                     buttonContent={changeInstalmentsMenuTextContents('modals.changeInstalmentsMenu.calendar.quickChoiceButtons.button1')} 
                     onClick={() => handleQuickChoiceButtonClick(quickChoiceButtonsData.TWO)} 
                  />
                  <QuickMonthsChoiceButton 
                     maxLoanPeriod={maxLoanPeriod}
                     quickMonthChoiceButtonValue={quickChoiceButtonsData.SIX}
                     buttonContent={changeInstalmentsMenuTextContents('modals.changeInstalmentsMenu.calendar.quickChoiceButtons.button2')}  
                     onClick={() => handleQuickChoiceButtonClick(quickChoiceButtonsData.SIX)} 
                  />
                  <QuickMonthsChoiceButton 
                     maxLoanPeriod={maxLoanPeriod}
                     quickMonthChoiceButtonValue={quickChoiceButtonsData.ALL}
                     buttonContent={changeInstalmentsMenuTextContents('modals.changeInstalmentsMenu.calendar.quickChoiceButtons.button3')} 
                     onClick={() => handleQuickChoiceButtonClick(quickChoiceButtonsData.ALL)} 
                  />
               </div>
            </div>
         }
      </div>
   )
}


/**
 * Link do dokumentacji: https://mui.com/material-ui/api/slide/
*/
const ChangeInstalmentsMenuModalDialogTransition = React.forwardRef(function Transition(props: TransitionProps & {children: React.ReactElement<any, any>}, ref: React.Ref<unknown>) {
   return <Slide direction="up" ref={ref} {...props} />
})

/**
 * Komponent loadera używany do blokowania kalendarza z wyborem miesięcy na czas oczekiwania na odpowiedź z Backendu
*/
const ChangeInstalmentsMenuModalContentLoader = ({ isLoading }: { isLoading: boolean }) => {

   const [canRender, setCanRender] = useState(false)
   const minimalRequestDelayToRenderLoader = 500

   useEffect(() => { // Opóźnienie - gdy request ładuje się dłużej niż ustalony próg czasowy, dopiero wtedy pokazujemy loadera
      setCanRender(false)
      const timeoutId = setTimeout(() => setCanRender(true), minimalRequestDelayToRenderLoader)

      return () => clearTimeout(timeoutId)
   
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [ isLoading ])
 

   if(isLoading && canRender) {
      return (
         <>
            <div className='select-instalments-months-content-loading-wrapper' />
            <div className='select-instalments-months-content-loader' />
         </>
      )
   }
   else {
      return null
   }
}

const ChangeInstalmentsMenu = () => {
   const analyticsEventTypesNames = useSelector((state: AppState) => state.globalConfigurationState.analyticsEventTypesNames)
   const changeInstalmentsMenuTextContents = useTextContents()

   const {
      isMobileDevice,
      isDialogOpen,
      isLoading,
      applicationState,
      monthsInstalmentTable,
      calendarInfoParams,
      
      dispatch,
      handleOnMenuClose,
      handleOnMenuOpen,
      getSelectInstalmentsMonthsHeading,
      handleRecalculateOffertOnMonthSelect
   } = useChangeInstalmentsMenuLogic()


   useEffect(() => {
      if(isDialogOpen && analyticsEventTypesNames !== undefined){
         analyticsService.sendAnalyticEvent(analyticsEventTypesNames.ChangeInstallmentsShow) // Odkładamy dane statystyczne o wejściu użytkownika na modala z wyborem ilości rat
      }
   }, [ isDialogOpen ])


   if(WorkflowTypes.isFlexi(applicationState.workflowId)) {
      return (
         <div>
            <Button 
               uiButtonType={AppButtonTypes.PRIMARY} 
               className='payment-details-short-change-instalments-button' 
               buttonContent={changeInstalmentsMenuTextContents('modals.changeInstalmentsMenu.openModalButton')} 
               onClick={ handleOnMenuOpen }
            />
            <Dialog
               scroll='body'
               fullScreen={isMobileDevice? true : false}
               TransitionComponent={ChangeInstalmentsMenuModalDialogTransition}
               open={isDialogOpen}
               classes={{ root: 'input-dropdown-menu-outer-wrapper', paper: 'input-dropdown-menu-container-wrapper' }}
               onBlur={event  => {}}
               onFocus={event => {}}
               onClose={ handleOnMenuClose }
            >
               <DialogTitle classes={{root: 'input-dropdown-menu-title'}}>
                  <img src={ExpandIcon} alt={"<"} className="input-dropdown-menu-title-back-icon" onClick={( handleOnMenuClose )} />
                  <div>
                     <h4>{changeInstalmentsMenuTextContents('modals.changeInstalmentsMenu.header.main')}</h4>
                     <p>{changeInstalmentsMenuTextContents('modals.changeInstalmentsMenu.header.aside')}</p>
                  </div>
               </DialogTitle>
               <div>
                  <div className='change-instalments-menu-outer-wrapper'>
                     <ol className='change-instalments-menu-description-steps'>
                        <li>{changeInstalmentsMenuTextContents('modals.changeInstalmentsMenu.descriptionSteps.step1')} {calendarInfoParams.currentMonthWithYear}</li>
                        <li>{changeInstalmentsMenuTextContents('modals.changeInstalmentsMenu.descriptionSteps.step2')}</li>
                        <li>{changeInstalmentsMenuTextContents('modals.changeInstalmentsMenu.descriptionSteps.step3')}</li>
                     </ol>
                     <SelectInstalmentsMonths
                        isCalendarValid={calendarInfoParams.isCalendarValid}
                        selectInstalmentsMonthsHeading={getSelectInstalmentsMonthsHeading()}
                        monthsInstalmentTable={monthsInstalmentTable}
                        handleRecalculateOffertOnMonthSelect={handleRecalculateOffertOnMonthSelect}
                        changeInstalmentsMenuTextContents={changeInstalmentsMenuTextContents}
                        maxLoanPeriod={applicationState.maxLoanPeriod}
                        dispatch={dispatch}
                        isLoading={isLoading}
                     />
                  </div>
                  <Button 
                     uiButtonType={AppButtonTypes.PRIMARY}
                     disabled={!calendarInfoParams.isCalendarValid || isLoading} 
                     buttonContent={changeInstalmentsMenuTextContents('modals.changeInstalmentsMenu.submitButtonText')}
                     className={clsx('change-instalments-menu-submit-button', isLoading && 'change-instalments-menu-submit-button-content-loading')} 
                     onClick={() => {
                        if(isLoading === false){
                           handleRecalculateOffertOnMonthSelect()
                           handleOnMenuClose()
                        }
                        else {
                           return
                        }
                     }}
                  />
               </div>
               {
                  isMobileDevice ? 
                  <DialogActions classes={{ root: 'input-dropdown-menu-bottom-back-button'}} onClick={ handleOnMenuClose }>
                     <p>{changeInstalmentsMenuTextContents('modals.changeInstalmentsMenu.cancelButtonText')}</p>
                  </DialogActions>
                  :
                  null
               }
            </Dialog>
         </div>
      )
   }
   else {
      return null
   }
}

export default ChangeInstalmentsMenu
